import React, { useCallback, useEffect, useRef, useState } from 'react';

function ContactModal({ open, onClose, returnFocusRef, formEndpoint = 'https://formspree.io/f/mpqjalgg' }) {
  const dialogRef = useRef(null);
  const firstInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error' | null

  const handleClose = useCallback(() => {
    onClose();
    // return focus to triggering element if provided
    setTimeout(() => returnFocusRef?.current?.focus(), 0);
  }, [onClose, returnFocusRef]);

  useEffect(() => {
    if (open) {
      setTimeout(() => firstInputRef.current?.focus(), 0);
      const onKey = (e) => {
        if (e.key === 'Escape') handleClose();
      };
      document.addEventListener('keydown', onKey);
      return () => document.removeEventListener('keydown', onKey);
    }
    // clear status when closed
    setStatus(null);
  }, [open, handleClose]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const data = new FormData(form);

    try {
      setSubmitting(true);
      const res = await fetch(formEndpoint, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: data,
      });

      if (res.ok) {
        setStatus('success');
        form.reset();
        // auto-close after a short delay and return focus
        setTimeout(() => {
          setSubmitting(false);
          handleClose();
        }, 1400);
      } else {
        setStatus('error');
        setSubmitting(false);
      }
    } catch (err) {
      setStatus('error');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-modal-title"
    >
      <div ref={dialogRef} className="bg-gray-900 border-2 border-neon rounded-md max-w-lg w-full p-6">
        <div className="flex justify-between items-start mb-4">
          <h3 id="contact-modal-title" className="text-lg neon-text">Contact Me</h3>
          <button
            aria-label="Close contact form"
            className="text-neon hover:text-white"
            onClick={handleClose}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <label className="text-sm" htmlFor="name">Name</label>
          <input ref={firstInputRef} id="name" name="name" required className="p-2 rounded bg-gray-800 border border-gray-700 focus:ring-2 focus:ring-neon outline-none" />

          <label className="text-sm" htmlFor="email">Email</label>
          <input id="email" name="email" type="email" required className="p-2 rounded bg-gray-800 border border-gray-700 focus:ring-2 focus:ring-neon outline-none" />

          <label className="text-sm" htmlFor="message">Message</label>
          <textarea id="message" name="message" rows="4" required className="p-2 rounded bg-gray-800 border border-gray-700 focus:ring-2 focus:ring-neon outline-none" />

          <div className="flex items-center justify-between mt-2">
            <button type="submit" disabled={submitting} className="px-4 py-2 border-2 border-neon neon-glow hover:bg-neon hover:text-black disabled:opacity-50" aria-busy={submitting}>
              {submitting ? 'Sending...' : 'Send'}
            </button>
            <button type="button" onClick={handleClose} className="text-sm text-gray-400">Cancel</button>
          </div>

          {status === 'success' && (
            <div className="mt-3 text-sm text-green-400">Message sent — thank you!</div>
          )}
          {status === 'error' && (
            <div className="mt-3 text-sm text-red-400">There was an issue sending your message. Please try emailing directly.</div>
          )}
        </form>
      </div>
    </div>
  );
}

export default ContactModal;